import React from 'react';
import { connect } from 'react-redux';
import { createActions } from '../../../../store';
import SliderListItem from '../../../../../../list-items/SliderListItem';
import CondensedListItem from '../../../../../../list-items/CondensedListItem';
import { previewHistoryBatch } from '../../../../store/extensions';
import { getMediaSourceSingleUrl } from '../../../../helpers';
import useImageElementNaturalSize from '../useImageElementNaturalSize';
import { calculateFramePercentages, calculateMaximumSizeFactor, calculateSizeFactorForPercentage } from '../imageElementSizeCalculations';
import { MAX_IMAGE_ELEMENT_FRAME_PERCENTAGE, MIN_IMAGE_ELEMENT_FRAME_PERCENTAGE } from '../../../../CreatePageConstants';

const NO_PERCENTAGES = { width: 0, height: 0 };

// Width and Height read as a share of the frame, and they move together: one size factor scales both
// axes, so a picture is never stretched out of its own proportions. Whichever axis fills the frame
// first is the one that reaches 100, and the other stops below it.
const ImageElementSizeListItem = (props) => {
    const { index, sizeFactor, imageUrl, aspectRatio, updateImageElement } = props;

    const naturalSize = useImageElementNaturalSize(imageUrl);

    const percentages = naturalSize ? calculateFramePercentages(sizeFactor, naturalSize, aspectRatio) : NO_PERCENTAGES;
    const maximumSizeFactor = naturalSize ? calculateMaximumSizeFactor(naturalSize, aspectRatio) : 0;
    const maximumPercentages = naturalSize ? calculateFramePercentages(maximumSizeFactor, naturalSize, aspectRatio) : NO_PERCENTAGES;

    const applyPercentage = (axis) => (percentage) => {
        if (!naturalSize)
            return;

        previewHistoryBatch.start();

        const requestedSizeFactor = calculateSizeFactorForPercentage(percentage, axis, naturalSize, aspectRatio);
        updateImageElement(index, { sizeFactor: Math.min(requestedSizeFactor, maximumSizeFactor) });
    };

    // A slider cannot offer more than the picture is allowed to grow to, and the axis that is not the
    // limiting one tops out below a full frame.
    const axisMaximum = (axis) => Math.max(Math.floor(maximumPercentages[axis]), MIN_IMAGE_ELEMENT_FRAME_PERCENTAGE);

    // Side by side on one row, the way X and Y Position and the shadow controls are laid out: the two
    // belong to one size and reading them as a pair is what the client asked for.
    return (
        <CondensedListItem
            elements={[
                {
                    width: 6,
                    component: <SliderListItem
                        title='Width'
                        value={percentages.width}
                        min={MIN_IMAGE_ELEMENT_FRAME_PERCENTAGE}
                        max={Math.min(axisMaximum('width'), MAX_IMAGE_ELEMENT_FRAME_PERCENTAGE)}
                        step={1}
                        disabled={!naturalSize}
                        onValueChange={applyPercentage('width')}
                        onSliderChangeCommitted={() => previewHistoryBatch.end()}
                        onInputFocusIn={() => previewHistoryBatch.start()}
                        onInputFocusOut={() => previewHistoryBatch.end()}
                    />
                },
                {
                    width: 6,
                    component: <SliderListItem
                        title='Height'
                        value={percentages.height}
                        min={MIN_IMAGE_ELEMENT_FRAME_PERCENTAGE}
                        max={Math.min(axisMaximum('height'), MAX_IMAGE_ELEMENT_FRAME_PERCENTAGE)}
                        step={1}
                        disabled={!naturalSize}
                        onValueChange={applyPercentage('height')}
                        onSliderChangeCommitted={() => previewHistoryBatch.end()}
                        onInputFocusIn={() => previewHistoryBatch.start()}
                        onInputFocusOut={() => previewHistoryBatch.end()}
                    />
                }
            ]}
        />
    );
};

const mapStateToProps = (state, ownProps) => {
    const { videoSettings } = state.createPreview.present;
    const imageElement = (videoSettings.elements.imageElements || videoSettings.elements.images)[ownProps.index];

    return {
        // Pictures added before the size control existed keep their fitted size.
        sizeFactor: imageElement.sizeFactor ?? 1,
        imageUrl: getMediaSourceSingleUrl(imageElement.mediaSource),
        aspectRatio: videoSettings.aspectRatio
    };
}

const mapDispatchToProps = (dispatch) => {
    const updateImageElement = (index, properties) => dispatch(createActions.updateImageElement(index, properties));
    return { updateImageElement }
}

export default connect(mapStateToProps, mapDispatchToProps)(ImageElementSizeListItem);
